//* Libraries imports
import { useEffect, useCallback } from "react";

//* Componentes imports
import { Button } from "@components/ui/button";
import { Card, CardContent } from "@components/ui/card";
import { ScrollArea, ScrollBar } from "@components/ui/scroll-area";

//* Types imports
import { IProductPhase, ActivityType } from "../types";
import { IProductExtended } from "../ProductFormNew";

type ProductFormMenuProps = {
  formData: Partial<IProductExtended>;
  phases: IProductPhase[];
  appointments: any;
  onSave: () => void;
  isSubmitting: boolean;
  tabSelect: number;
  setTabSelect: (tab: number) => void;
};

export const ProductFormMenu = ({
  formData,
  phases,
  appointments,
  onSave,
  isSubmitting,
  tabSelect,
  setTabSelect,
}: ProductFormMenuProps) => {
  const totalPhases = phases.length;
  const totalActivities = phases.reduce(
    (sum, phase) => sum + (phase.activities?.length || 0),
    0
  );
  const totalForms = phases.reduce(
    (sum, phase) =>
      sum +
      phase.activities.filter((activity) => activity.type === ActivityType.FORM)
        .length,
    0
  );
  const totalTechnicalDocs = phases.reduce(
    (sum, phase) =>
      sum +
      phase.activities.reduce(
        (acc, activity) =>
          acc +
          (activity.techinicals
            ? Object.values(activity.techinicals).length
            : 0),
        0
      ),
    0
  );

  const execScroll = useCallback(() => {
    const scrollBox = document.getElementById("scroll-box");
    if (!scrollBox) return;

    const scrollTop = scrollBox.scrollTop;

    if (scrollTop >= 937) {
      document.getElementById("fases-img")?.childNodes.forEach((elem: any) => {
        elem.setAttribute("fill", "#1777CF");
      });
    } else {
      document.getElementById("fases-img")?.childNodes.forEach((elem: any) => {
        elem.setAttribute("fill", "#7D8592");
      });
    }

    if (scrollTop >= 1700) {
      document
        .getElementById("atividades-img")
        ?.childNodes.forEach((elem: any) => {
          elem.setAttribute("fill", "#1777CF");
        });
    } else {
      document
        .getElementById("atividades-img")
        ?.childNodes.forEach((elem: any) => {
          elem.setAttribute("fill", "#7D8592");
        });
    }

    if (scrollTop >= 1900) {
      if (tabSelect === 0) {
        document
          .getElementById("pecas-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
      } else if (tabSelect === 1) {
        document
          .getElementById("pecas-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
        document
          .getElementById("forms-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
      } else if (tabSelect === 2) {
        document
          .getElementById("pecas-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
        document
          .getElementById("forms-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
        document
          .getElementById("agendas-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
      } else if (tabSelect === 3) {
        document
          .getElementById("pecas-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
        document
          .getElementById("forms-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
        document
          .getElementById("agendas-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
        document
          .getElementById("servicos-img")
          ?.childNodes.forEach((elem: any) => {
            elem.setAttribute("fill", "#1777CF");
          });
      }
    } else {
      document.getElementById("pecas-img")?.childNodes.forEach((elem: any) => {
        elem.setAttribute("fill", "#7D8592");
      });
      document.getElementById("forms-img")?.childNodes.forEach((elem: any) => {
        elem.setAttribute("fill", "#7D8592");
      });
      document
        .getElementById("agendas-img")
        ?.childNodes.forEach((elem: any) => {
          elem.setAttribute("fill", "#7D8592");
        });
      document
        .getElementById("servicos-img")
        ?.childNodes.forEach((elem: any) => {
          elem.setAttribute("fill", "#7D8592");
        });
    }
  }, [tabSelect]);

  useEffect(() => {
    execScroll();
    const scrollBox = document.getElementById("scroll-box");
    if (scrollBox) {
      scrollBox.onscroll = () => {
        execScroll();
      };
    }
    return () => {
      if (scrollBox) {
        scrollBox.onscroll = null;
      }
    };
  }, [execScroll]);

  const menuItems = [
    {
      icon: (
        <svg
          id="products-img"
          xmlns="http://www.w3.org/2000/svg"
          width={25}
          height="25"
          viewBox="0 0 20 16"
        >
          <path
            d="M0.662897 3.35419C0.220942 3.81256 0 4.36119 0 4.99998L0 13.6666C0 14.3057 0.220942 14.8543 0.662897 15.3125C1.10489 15.771 1.63392 16 2.24999 16H2.89279V2.66658H2.24999C1.63378 2.66658 1.10478 2.89582 0.662897 3.35419Z"
            fill="#1777CF"
          />
          <path
            d="M12.8571 0.999959C12.8571 0.722158 12.7634 0.486097 12.5758 0.291629C12.3885 0.097161 12.1607 0 11.8929 0L6.10719 0C5.83938 0 5.61172 0.097161 5.42417 0.291629C5.23665 0.485987 5.14292 0.722122 5.14292 0.999959V2.66658H3.8572V16H14.1429V2.66658H12.8571V0.999959ZM11.5715 2.66658H6.42861V1.33329H11.5715V2.66658Z"
            fill="#1777CF"
          />
          <path
            d="M17.3371 3.35419C16.8951 2.89586 16.366 2.66658 15.75 2.66658H15.1071V16H15.75C16.3661 16 16.8951 15.771 17.3371 15.3125C17.779 14.8543 18 14.3056 18 13.6666V4.99998C18 4.36119 17.7789 3.81256 17.3371 3.35419Z"
            fill="#1777CF"
          />
        </svg>
      ),
      label: "Produto",
      onclick: () =>
        document
          .getElementById("produtos")
          ?.scrollIntoView({ behavior: "smooth" }),
      value: formData.name || "Novo Produto",
      isTitle: true,
    },
    {
      icon: (
        <svg
          id="fases-img"
          xmlns="http://www.w3.org/2000/svg"
          width={25}
          height="25"
          viewBox="0 0 20 16"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.0725 0.60016V3.79598H9.26844V0.721711C9.26844 0.654605 9.32297 0.600098 9.39004 0.600098L21.0725 0.60016Z"
            fill="#3975C9"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18.8804 4.3012V7.49696H7.07638V4.42257C7.07638 4.35559 7.13091 4.30114 7.19798 4.30114L18.8804 4.3012Z"
            fill="#3975C9"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.6883 8.00231V11.1979H4.88425V8.12374C4.88425 8.05682 4.93878 8.00231 5.00585 8.00231H16.6883Z"
            fill="#3975C9"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.4962 11.7032V14.899H2.69213V11.8248C2.69213 11.7579 2.74659 11.7032 2.81373 11.7032L14.4962 11.7032Z"
            fill="#3975C9"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.3041 15.4043V18.6001H0.5V15.5259C0.5 15.459 0.55453 15.4043 0.621601 15.4043H12.3041V15.4043Z"
            fill="#3975C9"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.5 0.60016V18.6001H12.8096V15.4043H14.749C14.8886 15.4043 15.0017 15.2913 15.0017 15.1517V11.7033H16.9411C17.0807 11.7033 17.1938 11.5902 17.1938 11.4507V8.00231H19.1332C19.2729 8.00231 19.386 7.88912 19.386 7.74967V4.30127H21.3253C21.465 4.30127 21.5781 4.18807 21.5781 4.04862V0.60016H23.5Z"
            fill="#3975C9"
          />
        </svg>
      ),
      label: "Fases",
      onclick: () =>
        document
          .getElementById("fases")
          ?.scrollIntoView({ behavior: "smooth" }),
      value: String(totalPhases).padStart(2, "0"),
    },
    {
      icon: (
        <svg
          id="atividades-img"
          xmlns="http://www.w3.org/2000/svg"
          width={25}
          height="25"
          viewBox="0 0 20 16"
        >
          <path
            d="M17.6307 0.00157104C14.117 -0.0614313 10.1098 1.77539 7.57338 4.62475C5.14943 4.6705 2.78923 5.66429 1.05152 7.4021C0.950272 7.50185 0.914273 7.65111 0.958522 7.78612C1.00352 7.92187 1.12052 8.02012 1.26152 8.04037L4.15195 8.45439L3.79496 8.85416C3.66221 9.00266 3.66896 9.22842 3.80996 9.36943L8.6301 14.1899C8.70285 14.2626 8.79885 14.2994 8.8956 14.2994C8.98485 14.2994 9.07409 14.2679 9.14534 14.2041L9.54508 13.8471L9.95907 16.7377C9.97932 16.8787 10.0903 16.9822 10.2246 17.0272C10.2591 17.0385 10.2951 17.0437 10.3318 17.0437C10.4383 17.0437 10.5471 16.9972 10.6221 16.923C12.3358 15.2091 13.3295 12.8488 13.3752 10.4247C16.2274 7.88287 18.0791 3.87697 17.9974 0.367585C17.9921 0.168077 17.8309 0.00682124 17.6307 0.00157104ZM14.1747 6.47582C13.8095 6.84108 13.3295 7.02409 12.8488 7.02409C12.368 7.02409 11.888 6.84108 11.5228 6.47582C10.7923 5.74454 10.7923 4.55499 11.5228 3.82372C12.254 3.09244 13.4435 3.09244 14.1747 3.82372C14.906 4.55499 14.906 5.74529 14.1747 6.47582Z"
            fill="#7D8592"
          />
          <path
            d="M2.043 12.6785C1.24052 13.4811 0.13879 17.1067 0.0157927 17.517C-0.0239564 17.649 0.0127928 17.7922 0.109541 17.8897C0.181539 17.9618 0.276787 18 0.375035 18C0.411034 18 0.447033 17.9948 0.483032 17.9843C0.893273 17.8612 4.51869 16.7595 5.32118 15.9569C6.22491 15.0531 6.22491 13.5823 5.32118 12.6785C4.4167 11.7748 2.94673 11.7755 2.043 12.6785Z"
            fill="#7D8592"
          />
        </svg>
      ),
      label: "Atividades",
      onclick: () =>
        document
          .getElementById("atividades")
          ?.scrollIntoView({ behavior: "smooth" }),
      value: String(totalActivities).padStart(2, "0"),
    },
    {
      icon: (
        <svg
          id="pecas-img"
          xmlns="http://www.w3.org/2000/svg"
          width={80}
          height="80"
          viewBox="0 0 42 42"
          fill="none"
        >
          <path
            d="M12 30H24.6798V25.0013C24.6798 24.8221 24.8249 24.6767 25.0044 24.6767H30V12H12V30ZM14.9165 15.6397H27.0838C27.2633 15.6397 27.4084 15.7851 27.4084 15.9643C27.4084 16.1434 27.2633 16.2889 27.0838 16.2889H14.9165C14.737 16.2889 14.5919 16.1434 14.5919 15.9643C14.5919 15.7851 14.737 15.6397 14.9165 15.6397ZM14.9165 18.6519H27.0838C27.2633 18.6519 27.4084 18.7973 27.4084 18.9765C27.4084 19.1557 27.2633 19.3011 27.0838 19.3011H14.9165C14.737 19.3011 14.5919 19.1557 14.5919 18.9765C14.5919 18.7973 14.737 18.6519 14.9165 18.6519ZM14.9165 21.6641H27.0838C27.2633 21.6641 27.4084 21.8095 27.4084 21.9887C27.4084 22.1679 27.2633 22.3133 27.0838 22.3133H14.9165C14.737 22.3133 14.5919 22.1679 14.5919 21.9887C14.5919 21.8095 14.737 21.6641 14.9165 21.6641ZM14.9165 24.6763H21.6507C21.8302 24.6763 21.9753 24.8218 21.9753 25.0009C21.9753 25.1801 21.8302 25.3255 21.6507 25.3255H14.9165C14.737 25.3255 14.5919 25.1801 14.5919 25.0009C14.5919 24.8218 14.737 24.6763 14.9165 24.6763Z"
            fill="#7D8592"
          />
          <path
            d="M29.541 25.3255H25.3293V29.5375L29.541 25.3255Z"
            fill="#7D8592"
          />
        </svg>
      ),
      label: "Peças Técnicas",
      value: String(totalTechnicalDocs).padStart(2, "0"),
      onclick: () => {
        document
          .getElementById("aplicacoes")
          ?.scrollIntoView({ behavior: "smooth" });
        setTabSelect(0);
      },
    },
    {
      icon: (
        <svg
          id="forms-img"
          xmlns="http://www.w3.org/2000/svg"
          width={80}
          height="80"
          viewBox="0 0 42 42"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.9938 12.9C16.9938 12.4029 17.3908 12 17.8806 12H26.185C26.4321 12 26.6681 12.1047 26.8359 12.2888L29.7641 15.5006C29.9158 15.6669 30 15.8852 30 16.1118V29.1C30 29.5971 29.603 30 29.1132 30H17.8806C17.3908 30 16.9938 29.5971 16.9938 29.1V12.9ZM20.2454 18C20.0821 18 19.9498 18.1343 19.9498 18.3C19.9498 18.4657 20.0821 18.6 20.2454 18.6H26.4529C26.6161 18.6 26.7485 18.4657 26.7485 18.3C26.7485 18.1343 26.6161 18 26.4529 18H20.2454ZM19.9498 20.7C19.9498 20.5343 20.0821 20.4 20.2454 20.4H26.4529C26.6161 20.4 26.7485 20.5343 26.7485 20.7C26.7485 20.8657 26.6161 21 26.4529 21H20.2454C20.0821 21 19.9498 20.8657 19.9498 20.7ZM20.2454 22.8C20.0821 22.8 19.9498 22.9343 19.9498 23.1C19.9498 23.2657 20.0821 23.4 20.2454 23.4H26.4529C26.6161 23.4 26.7485 23.2657 26.7485 23.1C26.7485 22.9343 26.6161 22.8 26.4529 22.8H20.2454Z"
            fill="#7D8592"
          />
          <path
            d="M12.4274 16.3691L16.4025 13.9847V16.3247L13.3104 19.5435L12.1285 17.6051C11.8747 17.18 12.0086 16.6266 12.4274 16.3691Z"
            fill="#7D8592"
          />
          <path
            d="M16.4026 17.1842L12.4653 21.2827C12.1234 21.6386 12.1305 22.2084 12.4812 22.5554L16.4026 26.6321V17.1842Z"
            fill="#7D8592"
          />
        </svg>
      ),
      label: "Formulários",
      value: String(totalForms).padStart(2, "0"),
      onclick: () => {
        document
          .getElementById("aplicacoes")
          ?.scrollIntoView({ behavior: "smooth" });
        setTabSelect(1);
      },
    },
    {
      icon: (
        <svg
          id="agendas-img"
          xmlns="http://www.w3.org/2000/svg"
          width={80}
          height="80"
          viewBox="0 0 42 42"
          fill="none"
        >
          <path
            d="M11.5851 26.4H26.8983C27.0356 26.4 27.1683 26.3508 27.2736 26.2611C27.4207 26.135 30.7758 23.1796 30.9804 16.8H14.5343C14.3305 22.5909 11.2406 25.3129 11.2085 25.34C11.0203 25.5029 10.951 25.7689 11.0357 26.0056C11.1198 26.2418 11.339 26.4 11.5851 26.4Z"
            fill="#7D8592"
          />
          <path
            d="M30.414 13.2H27.4842V12.6C27.4842 12.264 27.2264 12 26.8982 12C26.5701 12 26.3123 12.264 26.3123 12.6V13.2H23.3434V12.6C23.3434 12.264 23.0855 12 22.7574 12C22.4293 12 22.1714 12.264 22.1714 12.6V13.2H19.2416V12.6C19.2416 12.264 18.9838 12 18.6557 12C18.3275 12 18.0697 12.264 18.0697 12.6V13.2H15.1399C14.8117 13.2 14.5539 13.464 14.5539 13.8V15.6H31V13.8C31 13.464 30.7421 13.2 30.414 13.2Z"
            fill="#7D8592"
          />
          <path
            d="M28.0261 27.181C27.7079 27.4523 27.3085 27.6 26.8983 27.6H14.5539V29.4C14.5539 29.7316 14.816 30 15.1399 30H30.414C30.7379 30 31 29.7316 31 29.4V22.6742C29.8703 25.5122 28.2938 26.9516 28.0261 27.181Z"
            fill="#7D8592"
          />
        </svg>
      ),
      label: "Agendas",
      value: String(appointments.length).padStart(2, "0"),
      onclick: () => {
        document
          .getElementById("aplicacoes")
          ?.scrollIntoView({ behavior: "smooth" });
        setTabSelect(2);
      },
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          id="servicos-img"
          width={80}
          height="80"
          viewBox="0 0 18 18"
          fill="none"
        >
          <path
            d="M10.332 1.314C10.332 2.0397 9.74369 2.628 9.018 2.628C8.29231 2.628 7.704 2.0397 7.704 1.314C7.704 0.588298 8.29231 0 9.018 0C9.74369 0 10.332 0.588298 10.332 1.314Z"
            fill={"#7D8592"}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.018 2.628L6.768 2.628C4.79311 2.628 3.11773 3.91046 2.52926 5.688H2.448C1.09601 5.688 0 6.78402 0 8.136C0 9.48798 1.09601 10.584 2.448 10.584H2.50611C3.07271 12.3988 4.76654 13.716 6.768 13.716H11.268C13.2695 13.716 14.9633 12.3988 15.5299 10.584H15.552C16.904 10.584 18 9.48798 18 8.136C18 6.78402 16.904 5.688 15.552 5.688H15.5067C14.9183 3.91046 13.2429 2.628 11.268 2.628L9.018 2.628ZM13.3807 7.848C13.3807 8.77093 12.6325 9.51916 11.7095 9.51916C10.7865 9.51916 10.0383 8.77093 10.0383 7.848C10.0383 6.92503 10.7865 6.17681 11.7095 6.17681C12.6325 6.17681 13.3807 6.92503 13.3807 7.848ZM6.27916 9.51916C7.20212 9.51916 7.95035 8.77093 7.95035 7.848C7.95035 6.92503 7.20212 6.17681 6.27916 6.17681C5.35622 6.17681 4.608 6.92503 4.608 7.848C4.608 8.77093 5.35622 9.51916 6.27916 9.51916Z"
            fill={"#7D8592"}
          />
          <path
            d="M3.23693 18C4.31543 15.9049 6.49937 14.472 9.018 14.472C11.5366 14.472 13.7206 15.9049 14.7991 18H3.23693Z"
            fill={"#7D8592"}
          />
        </svg>
      ),
      label: "Serviços IA",
      value: "00",
      isSpecial: true,
      onclick: () => {
        document
          .getElementById("aplicacoes")
          ?.scrollIntoView({ behavior: "smooth" });
        setTabSelect(3);
      },
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <Card
        style={{ height: window.innerHeight - 200, overflow: "scroll" }}
        className="custom-scrollbar w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors"
      >
        <CardContent className="p-6">
          <div className="flex flex-col h-full">
            {/* Menu Items */}
            <ScrollArea className="flex-1 -mx-2">
              <div className="px-2 space-y-6">
                <hr />
                <div style={{ marginTop: 15 }}>
                  <p
                    style={{
                      textAlign: "center",
                      color: "grey",
                      fontWeight: 400,
                    }}
                  >
                    Detalhes do Produto
                  </p>
                </div>
                <hr style={{ marginTop: 15 }} />
                <div
                  style={{
                    background: "url(/pontilhado.png)",
                    position: "absolute",
                    bottom: 240,
                    top: 67,
                    width: 100,
                    objectFit: "contain",
                    backgroundRepeat: "repeat-y",
                    backgroundSize: "262px 50px",
                    left: -58,
                  }}
                ></div>

                {menuItems.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      if (item.onclick) {
                        item.onclick();
                      }
                    }}
                    className={`flex items-center gap-4 p-3 rounded-lg transition-colors cursor-pointer`}
                  >
                    <div
                      className={`p-1 flex-shrink-0 flex items-center justify-center ${
                        item.isSpecial ? "bg-blue-100 p-2 rounded-lg" : ""
                      }`}
                      style={{
                        background: "#F4F4F4",
                        borderRadius: 10,
                        width: 40,
                        height: 40,
                        paddingLeft: 7,
                        zIndex: 1,
                      }}
                    >
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">{item.label}</p>
                      <p
                        className={`text-base truncate ${
                          item.isTitle
                            ? "font-semibold text-gray-900"
                            : "text-gray-700"
                        }`}
                      >
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}

                {/* Collaborators Section */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-4 p-3">
                    <img
                      src="/small/-cone-38.svg"
                      className="w-8 h-8"
                      alt="Colaboradores"
                    />
                    <div>
                      <p className="text-sm text-gray-600">Colaboradores</p>
                      <div className="flex -space-x-2 mt-2">
                        {/* Collaborator avatars would go here */}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2 p-4 rounded-xl hover:bg-gray-50"
                  >
                    <img
                      src="/small/-cone-35.svg"
                      className="size-6"
                      alt="smal"
                    />
                    <span className="text-xs text-center">Baixar Modelo</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2 p-4 rounded-xl hover:bg-gray-50"
                  >
                    <img
                      src="/small/-cone-7.svg"
                      className="size-6"
                      alt="smal"
                    />
                    <span className="text-xs text-center">Fazer Upload</span>
                  </Button>
                </div>
              </div>
              <ScrollBar />
            </ScrollArea>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        className="w-full h-14 mt-4 bg-blue-600 hover:bg-blue-700 rounded-xl text-white font-medium mb-4"
        onClick={onSave}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            <span>Salvando...</span>
          </div>
        ) : (
          "Salvar"
        )}
      </Button>
    </div>
  );
};
